import { z } from 'zod';
import type { ShapeManifest } from '../../../lib/diagram/shape-manifest.types';
import { resolveMediaSrc } from '../../media/utils/resolveMediaSrc';

const imageShapePropsSchema = z.object({
  objectKey: z.string().default(''),
  alt: z.string().optional(),
  caption: z.string().optional(),
  fit: z.enum(['cover', 'contain']).default('cover'),
  opacity: z.number().min(0).max(100).default(100),
  borderEnabled: z.boolean().default(true),
  borderColor: z.string().default('#CBD5E1'),
  borderWidth: z.number().min(0).max(12).default(1),
  borderDash: z.array(z.number()).optional(),
  dashEnabled: z.boolean().optional(),
  aspectRatioLocked: z.boolean().default(false),
  crop: z
    .object({
      x: z.number().min(0).max(1),
      y: z.number().min(0).max(1),
      width: z.number().min(0.01).max(1),
      height: z.number().min(0.01).max(1),
    })
    .optional(),
});

export type ImageShapeProps = z.infer<typeof imageShapePropsSchema>;

export const ImageShapeManifest: ShapeManifest<ImageShapeProps> = {
  id: 'image',
  version: '1.0.0',
  category: 'canvas',
  icon: 'image',
  propsSchema: imageShapePropsSchema,
  defaultProps: () => ({
    objectKey: '',
    fit: 'cover',
    opacity: 100,
    borderEnabled: true,
    borderColor: '#CBD5E1',
    borderWidth: 1,
    borderDash: [],
    dashEnabled: false,
    aspectRatioLocked: false,
  }),
  defaultDimensions: { width: 240, height: 160 },
  anchors: {
    snap: ['top', 'right', 'bottom', 'left', 'center'],
    perimeter: 'rect',
  },
  capabilities: {
    selectable: true,
    transformable: true,
    textEditable: false,
    connectable: true,
  },
  properties: [
    { key: 'objectKey', label: 'Image', kind: 'image', required: true, ui: { group: 'identity', priority: 1 } },
    { key: 'alt', label: 'Alt', kind: 'string', ui: { group: 'context', priority: 2 } },
    { key: 'caption', label: 'Caption', kind: 'string', ui: { group: 'context', priority: 3 } },
    { key: 'fit', label: 'Fit', kind: 'enum', enumValues: ['cover', 'contain'], ui: { group: 'extras', priority: 4 } },
    { key: 'opacity', label: 'Opacity', kind: 'number', ui: { group: 'extras', priority: 5 } },
    { key: 'borderEnabled', label: 'Border', kind: 'boolean', ui: { group: 'extras', priority: 6 } },
    { key: 'borderColor', label: 'Border color', kind: 'color', ui: { group: 'extras', priority: 7 } },
    { key: 'borderWidth', label: 'Border width', kind: 'number', ui: { group: 'extras', priority: 8 } },
    { key: 'borderDash', label: 'Border dash', kind: 'array', ui: { group: 'extras', priority: 9 } },
    { key: 'dashEnabled', label: 'Dash enabled', kind: 'boolean', ui: { group: 'extras', priority: 10 } },
    { key: 'aspectRatioLocked', label: 'Lock aspect ratio', kind: 'boolean', ui: { group: 'extras', priority: 11 } },
    { key: 'crop', label: 'Crop', kind: 'object', ui: { group: 'extras', priority: 12 } },
  ],
  acl: () => true,
  renderers: {
    svg: ({ viewModel, props, diagramId }) => {
      const width = Number(viewModel?.width ?? 0);
      const height = Number(viewModel?.height ?? 0);
      const safeWidth = Number.isFinite(width) && width > 0 ? width : 1;
      const safeHeight = Number.isFinite(height) && height > 0 ? height : 1;
      const propsRecord = props as unknown as Record<string, unknown>;
      const attrs = (propsRecord.attrs as Record<string, unknown> | undefined) ?? {};
      const objectKey =
        typeof propsRecord.objectKey === 'string'
          ? propsRecord.objectKey
          : typeof attrs.objectKey === 'string'
            ? attrs.objectKey
            : typeof attrs.photoRef === 'string'
              ? attrs.photoRef
              : '';
      const href = resolveMediaSrc(objectKey, { diagramId });
      const fit = typeof propsRecord.fit === 'string'
        ? propsRecord.fit
        : typeof attrs.fit === 'string'
          ? attrs.fit
          : props.fit;
      const preserveAspectRatio = fit === 'contain' ? 'xMidYMid meet' : 'xMidYMid slice';
      const opacity = Math.max(0, Math.min(100, Number(
        typeof propsRecord.opacity === 'number'
          ? propsRecord.opacity
          : typeof attrs.opacity === 'number'
            ? attrs.opacity
            : 100,
      ))) / 100;
      const borderEnabled = typeof propsRecord.borderEnabled === 'boolean'
        ? propsRecord.borderEnabled
        : typeof attrs.borderEnabled === 'boolean'
          ? attrs.borderEnabled
          : true;
      const borderColor = typeof propsRecord.borderColor === 'string'
        ? propsRecord.borderColor
        : typeof attrs.borderColor === 'string'
          ? attrs.borderColor
          : '#CBD5E1';
      const borderWidth = Math.max(0, Number(
        typeof propsRecord.borderWidth === 'number'
          ? propsRecord.borderWidth
          : typeof attrs.borderWidth === 'number'
            ? attrs.borderWidth
            : 1,
      ));
      const borderDashRaw = Array.isArray(propsRecord.borderDash)
        ? propsRecord.borderDash
        : Array.isArray(attrs.borderDash)
          ? attrs.borderDash
          : Array.isArray(attrs.dash)
            ? attrs.dash
            : [];
      const borderDash = borderDashRaw
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value) && value > 0);
      const dashEnabledRaw = typeof propsRecord.dashEnabled === 'boolean'
        ? propsRecord.dashEnabled
        : typeof attrs.dashEnabled === 'boolean'
          ? attrs.dashEnabled
          : undefined;
      const borderDashEnabled = (dashEnabledRaw ?? borderDash.length > 0) && borderDash.length > 0;
      const borderDashAttr = borderDashEnabled
        ? ` stroke-dasharray="${borderDash.map((value) => Number(value.toFixed(2))).join(' ')}"`
        : '';
      const caption = typeof propsRecord.caption === 'string'
        ? propsRecord.caption
        : typeof attrs.caption === 'string'
          ? attrs.caption
          : '';
      const cropInput = (propsRecord.crop as Record<string, unknown> | undefined) ?? (attrs.crop as Record<string, unknown> | undefined);
      const cropRect = cropInput
        ? {
            x: Number(cropInput.x ?? 0),
            y: Number(cropInput.y ?? 0),
            width: Number(cropInput.width ?? 1),
            height: Number(cropInput.height ?? 1),
          }
        : null;
      const hasCrop =
        !!cropRect &&
        Number.isFinite(cropRect.x) &&
        Number.isFinite(cropRect.y) &&
        Number.isFinite(cropRect.width) &&
        Number.isFinite(cropRect.height) &&
        cropRect.width > 0 &&
        cropRect.height > 0;
      const cropX = hasCrop ? Math.max(0, Math.min(1, cropRect!.x)) : 0;
      const cropY = hasCrop ? Math.max(0, Math.min(1, cropRect!.y)) : 0;
      const cropWidth = hasCrop ? Math.max(0.01, Math.min(1 - cropX, cropRect!.width)) : 1;
      const cropHeight = hasCrop ? Math.max(0.01, Math.min(1 - cropY, cropRect!.height)) : 1;
      const imageWidth = hasCrop ? safeWidth / cropWidth : safeWidth;
      const imageHeight = hasCrop ? safeHeight / cropHeight : safeHeight;
      const imageX = hasCrop ? -cropX * imageWidth : 0;
      const imageY = hasCrop ? -cropY * imageHeight : 0;
      const rawShapeId = typeof propsRecord.__exportShapeId === 'string'
        ? (propsRecord.__exportShapeId as string)
        : 'image';
      const clipId = `clip-image-shape-${rawShapeId.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
      const escapedHref = href
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/'/g, '&apos;');

      return [
        `<clipPath id="${clipId}"><rect x="0" y="0" width="${safeWidth}" height="${safeHeight}" rx="8" ry="8" /></clipPath>`,
        `<rect x="0" y="0" width="${safeWidth}" height="${safeHeight}" rx="8" ry="8" fill="#F8FAFC" stroke="${borderEnabled ? borderColor : 'transparent'}" stroke-width="${borderEnabled ? borderWidth : 0}"${borderEnabled ? borderDashAttr : ''} />`,
        `<image x="${imageX}" y="${imageY}" width="${imageWidth}" height="${imageHeight}" href="${escapedHref}" preserveAspectRatio="${hasCrop ? 'none' : preserveAspectRatio}" clip-path="url(#${clipId})" opacity="${opacity}" />`,
        caption
          ? `<rect x="0" y="${Math.max(0, safeHeight - 24)}" width="${safeWidth}" height="24" rx="0" ry="0" fill="rgba(15,23,42,0.65)" clip-path="url(#${clipId})" />`
          : '',
        caption
          ? `<text x="8" y="${Math.max(0, safeHeight - 8)}" fill="#FFFFFF" font-size="12" font-family="Noto Sans, sans-serif">${caption
              .replace(/&/g, '&amp;')
              .replace(/</g, '&lt;')
              .replace(/>/g, '&gt;')
              .replace(/"/g, '&quot;')
              .replace(/'/g, '&apos;')}</text>`
          : '',
      ].join('');
    },
  },
};
